import { createSelector } from 'reselect';
import { shuffle } from 'underscore/modules';
import { campaignsWithImageTemplatesSelector } from '../campaigns';
import { CURRENCIES, INTERNAL_PATH, SLOT_TYPES } from '../../constants';
import { copyLootpackWithCampaignImages } from '../campaigns/utils';
import { isInternal } from '../../utils';
import {
  hasNoPaidOrdersSelector,
  isSubscribedSelector,
  mappedPurchasesSelector,
  newsletterEmailSelector,
  shouldInviteUsersSelector,
} from '../../selectors';
import { hasShrededCardsSelector } from '../cards';
import { slotsSliceName } from './types';
import { canShowOfferwallBanner } from './utils';

export const slotsPlainSelector = ({ [slotsSliceName]: slots }) => slots;

export const slotsSelector = createSelector(
  slotsPlainSelector,
  mappedPurchasesSelector,
  hasNoPaidOrdersSelector,
  newsletterEmailSelector,
  hasShrededCardsSelector,
  shouldInviteUsersSelector,
  campaignsWithImageTemplatesSelector,
  isSubscribedSelector,
  (
    { items: slots },
    { boughtFree, purchases },
    hasNoPaidOrders,
    newsletterEmail,
    hasShrededCards,
    shouldInviteUsers,
    campaigns,
    isSubscribed,
    // eslint-disable-next-line max-params
  ) =>
    slots
      .map((slot) => {
        const { banners, loots, randomizedDisplay, type } = slot;
        const mappedSlot = {
          ...slot,
        };

        if (type === SLOT_TYPES.LOOT_SLOT) {
          mappedSlot.loots =
            loots &&
            loots
              .filter(
                ({ firstBuyer, repurchaseble, currency, _id }) =>
                  (repurchaseble || !purchases[_id]) &&
                  (!hasNoPaidOrders || !firstBuyer) &&
                  (currency !== CURRENCIES.FREE_CURRENCY || !boughtFree),
              )
              .map(copyLootpackWithCampaignImages(campaigns));

          if (randomizedDisplay) {
            mappedSlot.loots = shuffle(mappedSlot.loots);
          }
          return mappedSlot;
        }

        if (
          type === SLOT_TYPES.BANNER_SLOT ||
          type === SLOT_TYPES.BANNER_SLOT_2
        ) {
          mappedSlot.banners =
            banners &&
            banners.filter(
              ({ firstBuyer, uri, isAd }) =>
                (!uri.includes(`${INTERNAL_PATH}/offerwall/`) ||
                  canShowOfferwallBanner(
                    uri.replace(`${INTERNAL_PATH}/offerwall/`, ''),
                  )) &&
                (!hasNoPaidOrders || !firstBuyer) &&
                (!newsletterEmail ||
                  !(isInternal(uri) && uri.includes('/newsletter'))) &&
                !(isAd && isSubscribed),
            );
          if (randomizedDisplay) {
            mappedSlot.banners = mappedSlot.banners.filter(({ uri }) => {
              const hintType = uri.replace('lootboy://tooltip/', '');
              switch (hintType) {
                case 'invite':
                  return shouldInviteUsers;
                case 'shred':
                  return !hasShrededCards;
                default:
                  return true;
              }
            });
          }
          return mappedSlot;
        }

        return slot;
      })
      .filter(({ type, loots, videoId, banners }) => {
        if (type === SLOT_TYPES.LOOT_SLOT) {
          return loots.length;
        }
        if (type === SLOT_TYPES.YOUTUBE_SLOT) {
          return videoId;
        }
        if (
          type === SLOT_TYPES.BANNER_SLOT ||
          type === SLOT_TYPES.BANNER_SLOT_2
        ) {
          return banners.length;
        }
        return true;
      }),
);

export const createSlotsSelector = (page, selector = slotsSelector) =>
  createSelector(selector, (slots) =>
    slots.filter(({ placement }) => placement === page),
  );
